import { Callout } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useSpainClosurePeriods } from 'src/core/modules/bookkeep/apis/useSpainClosurePeriods';
import { type SpanishDprSettingsResponse } from 'src/core/modules/bookkeep/apis/useSpanishDprSettings';
import { useSpanishDprStartDateQuery } from 'src/core/modules/bookkeep/apis/useSpanishDprStartDate';

import { SpanishDprSetupEndYearDate } from './SpanishDprEndYeadDate/SpanishDprSetupEndYearDate';
import { SpanishDprUpdateEndYeadDate } from './SpanishDprEndYeadDate/SpanishDprUpdateEndYeadDate';
import { SpanishDprPeriodsList } from './SpanishDprPeriodsList';
import {
  type SanitizedSpainClosurePeriod,
  sanitizePeriodsList,
} from './helpers/sanitizePeriodsList';

export function SpanishDprArchiving({
  currentSettings,
}: {
  currentSettings: SpanishDprSettingsResponse | undefined;
}) {
  const { t } = useTranslation('global');
  const [periods, setPeriods] = useState<SanitizedSpainClosurePeriod[]>([]);

  const { date: firstSpanishDprPayableDate, isLoading: isLoadingStartDate } =
    useSpanishDprStartDateQuery();

  const { periods: closurePeriods, isLoading: isLoadingClosurePeriods } =
    useSpainClosurePeriods();
  const {
    fiscalYearEndMonth: monthFromSettings = null,
    fiscalYearEndDay: dayFromSettings = null,
  } = currentSettings?.settings || {};

  const fiscalYearEndSet =
    monthFromSettings !== null && dayFromSettings !== null;

  useEffect(() => {
    if (fiscalYearEndSet) {
      const sanitizedPeriodsList = sanitizePeriodsList(
        monthFromSettings,
        dayFromSettings,
        firstSpanishDprPayableDate,
        closurePeriods,
      );
      setPeriods(sanitizedPeriodsList);
    }
  }, [
    monthFromSettings,
    dayFromSettings,
    firstSpanishDprPayableDate,
    closurePeriods,
  ]);

  if (isLoadingStartDate || isLoadingClosurePeriods) {
    return null;
  }

  const isDateEditable = !periods.some((period) =>
    ['completed', 'ongoing', 'pending', 'failed'].includes(period.state),
  );

  if (fiscalYearEndSet) {
    return (
      <>
        <SpanishDprUpdateEndYeadDate
          initialMonth={monthFromSettings}
          initialDay={dayFromSettings}
          isEditable={isDateEditable}
        />
        {!firstSpanishDprPayableDate && (
          <Callout
            title={t('bookkeep.settings.spanishDpr.exportNotice')}
            variant="info"
            className="mt-24"
          />
        )}
        <SpanishDprPeriodsList periods={periods} />
      </>
    );
  }

  return <SpanishDprSetupEndYearDate />;
}
